import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import PageLayout from "../components/PageLayout";
import { PosterImage } from "../components/PosterImage";
import { useCatalog } from "../contexts/CatalogContext";
import { Playlists } from "../lib/api";
import { decodeMaybeUtf8 } from "../lib/decode";
import type { Stream } from "../lib/types";
import { blockWidthPx, offsetPx, ticksFor, windowStartFor } from "../lib/guideAxis";
import { fmtTimeIn } from "../lib/prefs";

/**
 * EPG guide grid — channels down, time across.
 *
 * The web client has never had one; only the Android app did. Two constraints
 * shaped this:
 *
 *  - **Panel load.** A guide wants EPG for every channel, and a real playlist
 *    has thousands. Rows fetch only when scrolled into view, results are cached
 *    for the tab's life, and channels with no `epg_channel_id` never fetch.
 *    Same discipline as the phone channel rows.
 *  - **Payload.** `/epg/full/` is ~127 KB per channel. The grid uses
 *    `/epg/window/`, which slices the same cached data down to the visible
 *    hours.
 *
 * Layout is a CSS grid per row rather than absolute positioning: a programme's
 * width is its duration, so the row is just a flex track scaled by MIN_PX.
 */

const HOURS = 4;
const MIN_PX = 6; // pixels per minute -> a 4h window is 1440px of track
const CHANNEL_COL = 200;
const ROW_H = 64;

type Programme = { title?: string; start: number; stop: number };

const cache = new Map<string, Programme[] | Promise<Programme[]>>();

async function loadWindow(pid: number, streamId: number): Promise<Programme[]> {
  const key = `${pid}:${streamId}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const p = Playlists.epgWindow(pid, streamId, HOURS)
    .then((d) => {
      const v = d?.programmes || [];
      cache.set(key, v);
      return v;
    })
    .catch(() => {
      cache.set(key, []);
      return [] as Programme[];
    });
  cache.set(key, p);
  return p;
}

function GuideRow({
  channel,
  pid,
  windowStart,
  onPick,
}: {
  channel: Stream;
  pid: number;
  windowStart: number;
  onPick: (c: Stream, p: Programme) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [progs, setProgs] = useState<Programme[] | null>(null);
  const hasEpg = !!channel.epg_channel_id;

  useEffect(() => {
    // No EPG id means there is nothing to fetch — resolve immediately to the
    // empty state rather than leaving a skeleton pulsing forever.
    if (!hasEpg) {
      setProgs([]);
      return;
    }
    if (!ref.current) return;
    const el = ref.current;
    let cancelled = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        loadWindow(pid, Number(channel.id)).then((v) => {
          if (!cancelled) setProgs(v);
        });
      },
      { root: null, rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [pid, channel.id, hasEpg]);

  return (
    <div ref={ref} className="flex" style={{ height: ROW_H }}>
      <div
        className="flex-shrink-0 sticky left-0 z-10 bg-canvas border-r border-line flex items-center gap-2 px-2"
        style={{ width: CHANNEL_COL }}
      >
        <div className="w-9 h-9 flex-shrink-0 rounded-chip bg-elevated overflow-hidden">
          <PosterImage
            src={channel.logo || undefined}
            alt={channel.name}
            className="w-full h-full"
            imgClass="w-full h-full object-contain p-0.5"
            orientation="row"
          />
        </div>
        <span className="text-[12px] font-ui text-text-primary line-clamp-2 leading-tight">
          {channel.name}
        </span>
      </div>

      <div className="flex gap-[2px] py-1 pl-[2px]">
        {progs === null ? (
          <div
            className="rounded-chip bg-surface animate-pulse"
            style={{ width: HOURS * 60 * MIN_PX - 4 }}
          />
        ) : progs.length === 0 ? (
          <div
            className="rounded-chip bg-surface/60 flex items-center px-3 text-[12px] font-ui text-text-muted"
            style={{ width: HOURS * 60 * MIN_PX - 4 }}
          >
            No guide data
          </div>
        ) : (
          progs.map((p, i) => {
            // Clip the leading programme to the window so the row starts at
            // "now" instead of hanging off the left edge.
            const widthPx = blockWidthPx(p.start, p.stop, windowStart, MIN_PX);
            return (
              <button
                key={`${p.start}-${i}`}
                type="button"
                onClick={() => onPick(channel, p)}
                title={decodeMaybeUtf8(p.title)}
                className="rounded-chip bg-surface hover:bg-elevated border border-line text-left px-2 overflow-hidden transition-colors duration-press ease-bz focus-visible:outline-none"
                style={{ width: widthPx - 2 }}
              >
                <span className="block text-[12px] font-ui text-text-primary truncate leading-tight mt-1">
                  {decodeMaybeUtf8(p.title)}
                </span>
                <span className="block font-data text-[10px] text-text-muted">
                  {fmtTimeIn(p.start * 1000)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function GuidePage() {
  const { catalog, activePlaylist, loading } = useCatalog();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(40);

  // One window origin for the whole grid, rounded down to the half hour so the
  // time ruler lands on readable marks.
  const windowStart = useMemo(() => windowStartFor(Math.floor(Date.now() / 1000)), []);

  const channels = useMemo(
    () => (catalog?.live?.streams || []).slice(0, limit),
    [catalog, limit],
  );

  const ticks = useMemo(
    // fmtTimeIn, not toLocaleTimeString: the latter takes neither the user's
    // configured timezone (Settings has a picker for it) nor a fixed hour
    // cycle, so the guide was the one surface that ignored the setting and
    // flipped between 12h and 24h with the device locale.
    () => ticksFor(windowStart, HOURS, MIN_PX, (epochS) => fmtTimeIn(epochS * 1000)),
    [windowStart],
  );

  const nowLeft = offsetPx(Math.floor(Date.now() / 1000), windowStart, MIN_PX);

  const onPick = (c: Stream, p: Programme) => {
    const now = Date.now() / 1000;
    if (p.start <= now && now < p.stop) {
      navigate(`/watch/live/${encodeURIComponent(c.id)}`);
    } else if (c.tv_archive === 1 && p.stop <= now) {
      navigate(`/catchup/${encodeURIComponent(c.id)}`);
    } else {
      // Future programme with no archive: the channel is the only thing we can
      // usefully open.
      navigate(`/watch/live/${encodeURIComponent(c.id)}`);
    }
  };

  if (!activePlaylist) {
    return (
      <PageLayout>
        <div className="px-4 md:px-8 py-4">
          <EmptyState title="Pick a playlist" cta={{ to: "/settings", label: "Open settings" }} />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.02em] text-text-primary mb-1">
          Guide
        </h1>
        <p className="font-data text-[11px] uppercase tracking-[0.13em] text-text-muted mb-4">
          Next {HOURS} hours · {channels.length} channels
        </p>

        {loading && channels.length === 0 ? (
          <p className="text-[14px] font-ui text-text-secondary py-12">Loading channels…</p>
        ) : channels.length === 0 ? (
          <EmptyState title="No live channels" hint="This playlist has no live streams." />
        ) : (
          <div className="overflow-x-auto border border-line rounded-card bg-canvas">
            <div style={{ width: CHANNEL_COL + HOURS * 60 * MIN_PX }}>
              {/* Time ruler */}
              <div className="flex sticky top-0 z-20 bg-elevated border-b border-line">
                <div
                  className="flex-shrink-0 sticky left-0 z-10 bg-elevated border-r border-line font-data text-[10px] uppercase tracking-[0.13em] text-text-muted flex items-center px-2"
                  style={{ width: CHANNEL_COL, height: 32 }}
                >
                  Channel
                </div>
                <div className="relative flex-1" style={{ height: 32 }}>
                  {ticks.map((t) => (
                    <span
                      key={t.left}
                      className="absolute top-0 h-full flex items-center font-data text-[10px] text-text-secondary border-l border-line pl-1"
                      style={{ left: t.left }}
                    >
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative">
                {/* Now-line, drawn over the rows */}
                <div
                  className="absolute top-0 bottom-0 w-[2px] bg-accent-gold z-[15] pointer-events-none"
                  style={{ left: CHANNEL_COL + nowLeft }}
                  aria-hidden="true"
                />
                {channels.map((c) => (
                  <GuideRow
                    key={c.id}
                    channel={c}
                    pid={activePlaylist.id}
                    windowStart={windowStart}
                    onPick={onPick}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {catalog && (catalog.live?.streams?.length || 0) > channels.length && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setLimit((n) => n + 40)}
              className="px-5 py-2.5 rounded-pill border border-line-strong text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-primary hover:border-accent-gold hover:text-accent-gold transition-fast"
            >
              Show more channels
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
